import Http from './axios-config';

class AppointmentsService {

    constructor() {
        this.http = new Http();
    }

    /**
     *
     * @param {*} date
     */
    async getAppointmentsSessionByDate(date, extraParams = {}) {
        return await this.http.get("/clinical-admins/appointments/sessions", {
            params: { date: date, ...extraParams }
        });
    }

    async createAppointmentsSession(session) {
        return await this.http.post("/clinical-admins/appointments/sessions", session);
    }

    async deleteAppointmentSession(id, values) {
        return await this.http.delete(`/clinical-admins/appointments/sessions/${id}`, {
            disableToast: true,
            data: values
        });
    }

    async getAppointmentsSlotsByDate(params) {
        return await this.http.get(`/clinical-admins/appointments/slots`, { params });
    }

    async blockUnblockAppointmentSlot(id) {
        return await this.http.patch(`/clinical-admins/appointments/slots/${id}/blocked`);
    }

    async setDNAStatus(id, status) {
        return await this.http.patch(`/clinical-admins/appointments/slots/${id}/status`, status);
    }

    async sendConsultation(id, notes) {
        return await this.http.post(`/clinical-admins/appointments/slots/${id}/consultations`, notes);
    }

    async editConsultation(id, notes) {
        return await this.http.put(`/clinical-admins/appointments/slots/${id}/consultations`, notes);
    }

    /**
     * Record the mental-health question for ONE consultation.
     *
     * The answer belongs to this consultation and no other: a patient may be recorded as not a
     * mental health consultation today and as one next month, and both stay true of the
     * consultation they belong to. The backend keys the row by appointment slot and rejects a plan
     * sent alongside an answer of "no", so send exactly what the clinician chose.
     *
     * @param {number} id appointment slot id
     * @param {{isMentalHealth: boolean, plan?: number, followUpDueAt?: string}} review
     */
    /**
     * Read the mental-health answer for ONE consultation.
     *
     * A 404 means the question was never answered for this consultation, which is NOT the same as
     * an answer of "no" - the backend stores those differently and the UI must not conflate them.
     *
     * @param {number} id appointment slot id
     */
    async getMentalHealthReview(id) {
        return await this.http.get(`/clinical-admins/appointments/slots/${id}/mental-health-review`, {
            disableToast: true,
        });
    }

    /**
     * Stop this consultation's follow-up reminder before it is sent.
     *
     * The answer itself is kept - what was recorded, and that it was stopped, both stay on the
     * record. Only the pending email is cancelled.
     *
     * @param {number} id appointment slot id
     */
    async stopMentalHealthReminders(id) {
        return await this.http.delete(`/clinical-admins/appointments/slots/${id}/mental-health-review`);
    }

    async setMentalHealthReview(id, review) {
        return await this.http.put(`/clinical-admins/appointments/slots/${id}/mental-health-review`, review);
    }

    async getAppointmentComments(id) {
        return await this.http.get(`/clinical-admins/appointments/slots/${id}/comments`);
    }

    async addComment(id, comment) {
        return await this.http.post(`/clinical-admins/appointments/slots/${id}/comments`, comment);
    }

    async rescheduleAppointment(id, values) {
        return await this.http.put(`/clinical-admins/appointments/slots/${id}/schedule`, values);
    }

    async createAppointment(id, values) {
        return await this.http.put(`/clinical-admins/appointments/slots/${id}/patients`, values);
    }

    async deleteAppointment(id) {
        return await this.http.delete(`/clinical-admins/appointments/slots/${id}`);
    }

    async getAppointment(id) {
        return await this.http.get(`/clinical-admins/appointments/slots/${id}`);
    }

    async squeezeAppointmentSlot(id) {
        return await this.http.put(`/clinical-admins/appointments/slots/${id}/squeeze`);
    }

    async updateAppointmentActualStartsTime(id, time) {
        return await this.http.put(`/clinical-admins/appointments/slots/${id}/times`, { actualStartsAt: time });
    }

    async updateAppointmentActualEndsTime(id, time) {
        return await this.http.put(`/clinical-admins/appointments/slots/${id}/times`, { actualEndsAt: time });
    }

    async editAppointmentSession(id, values) {
        return await this.http.put(`/clinical-admins/appointments/sessions/${id}`, values);
    }

    async getSessionById(id) {
        return await this.http.get(`/clinical-admins/appointments/sessions/${id}`);
    }

    async sendTrustPilot(id) {
        return await this.http.post(`/clinical-admins/appointments/${id}/trust-pilot/email`);
    }

    async showHideConsultationNotes(id, isHidden) {
        return await this.http.patch(`/clinical-admins/appointments/slots/${id}/consultations/is-hide`, {
            isHidden
        });
    }

    async getTakenTimeSlots(params) {
        return await this.http.get("/clinical-admins/appointments/sessions/timeslots", { params });
    }

    async getTakenPriorities(params) {
        return await this.http.get("/clinical-admins/appointments/sessions/priority", { params });
    }

}

export default new AppointmentsService();
